// <purpose>THE canonical FE role union. Not an authority: mirrors the BE projection
// (smarttix.core infra/scripts/role-capabilities.json), pinned by
// roleRegistry.contract.test.ts locally and by the deploy pre-flight equality check
// at the fleet chokepoint. After the harmonisation sweep, nothing else in
// frontend/src may enumerate roles.</purpose>
import type { RowLayout } from '../context/ClassificationSchemaContext';

export const KNOWN_ROLES = ['gmsb', 'vnb', 'wim', 'wmsb'] as const;

export type KnownRole = typeof KNOWN_ROLES[number];

export function isKnownRole(v: string): v is KnownRole {
  return (KNOWN_ROLES as readonly string[]).includes(v);
}

// gMSB/wMSB predate the vocabulary/schema seam: their K-code vocabulary is not a fallback
// borrowed from somewhere else, it is THEIR OWN rendered truth (see categoryMeta.ts's
// CATEGORY_LABELS tier). Every site that special-cases these two roles keys to this one
// predicate rather than repeating the 'gmsb' || 'wmsb' comparison — the FE role-literal scan
// (roleFlavouringScan.test.ts) treats a role-name string literal outside this declared home as
// drift, so a second inline comparison would both duplicate this fact and re-trip the scan.
const MSB_K_VOCABULARY_ROLES: ReadonlySet<KnownRole> = new Set(['gmsb', 'wmsb']);

export function ownsMsbKVocabulary(role: string | null | undefined): boolean {
  return !!role && MSB_K_VOCABULARY_ROLES.has(role as KnownRole);
}

// Role display names are registry CONTENT (the registry knows what its ids are called);
// a component spelling these itself would re-enumerate roles. Partial: wim has no testgen
// surface today — absent means the caller's own fallback, never an invented name.
export const ROLE_DISPLAY_LABELS: Partial<Record<KnownRole, string>> = {
  gmsb: 'gMSB (grundzuständiger Messstellenbetrieb)',
  wmsb: 'wMSB (wettbewerblicher Messstellenbetrieb)',
  vnb:  'VNB (Verteilnetzbetrieb)',
};

// Declarative visibility sets for role-conditional UI (config keys, tabs). Same doctrine as
// MSB_K_VOCABULARY_ROLES: sites reference the set, never re-type the role names.
export const MSB_ROLES: readonly KnownRole[] = ['gmsb', 'wmsb'];
export const VNB_ONLY: readonly KnownRole[] = ['vnb'];
// Roles whose reply engine writes the fixed danger letters, so the backend requires the two
// emergency contact numbers for them (the VNB flavour and, since part 5-2, WIM).
export const DANGER_LETTER_ROLES: readonly KnownRole[] = ['vnb', 'wim'];

// The row-layout default is a PRESENTATION decision, declared per role and exhaustively — it is
// NOT derived from any other role fact. It used to read ownsMsbKVocabulary above, which answers a
// different question (does this role own the legacy K vocabulary); a role that owns no K
// vocabulary but isn't vnb either silently inherited that ternary's other branch, which is exactly
// how a third role ended up on the wrong default. Exhaustive Record<KnownRole, …> (not a set of
// exceptions) means a fifth role with no entry here is a compile error, not a silent inheritance.
export const ROW_LAYOUT_DEFAULT_BY_ROLE: Readonly<Record<KnownRole, RowLayout>> = {
  gmsb: 'strip_abbr',
  wmsb: 'strip_abbr',
  // wim vocabulary v7, 2026-09-13 (operator ruling): FLIPPED from strip_abbr. A clerk testing the
  // triage flow could not relate the strip's two-letter Kürzel to the work in front of them — the
  // handling axis is a DECISION ("who answers this"), and a decision read at a glance needs its
  // word, not a code the reader has to learn first. So a wim row states the served label in a badge
  // on line 2 by default. The initialisms are NOT deleted: they stay one setting away
  // (UI_ROW_LAYOUT=strip_abbr), where the strip wears the served icon when the tenant serves one
  // and the served Kürzel otherwise — a denser face for a reader who already knows the vocabulary.
  wim: 'strip_label',
  vnb: 'strip_label',
};

// Declared identity fact (the C# instrument's precedent: a vnb-contract check is a declared
// fact, not a proxy). Named predicate so call sites carry no role literal.
export function isVnb(role: string | null | undefined): boolean {
  return role === 'vnb';
}

// ── Schema-contract projection ────────────────────────────────────────────────────────────────
// The BE's per-role `contract` (core `RoleRegistry.SchemaContractKind`, projected into
// infra/scripts/role-capabilities.json as roles.<role>.contract) mirrored here EXACTLY as
// KNOWN_ROLES mirrors the union — same doctrine, same pin: roleRegistry.contract.test.ts asserts
// equality against the committed projection, so this map cannot drift from the backend that
// decides it. /config serves the tenant's ROLE, never its contract kind; the role→contract step
// is this projection's job, which is why the capability predicates below live here and not at a
// call site.
export type SchemaContractKind = 'msbvocabulary' | 'vnb' | 'wim';

export const ROLE_CONTRACTS: Readonly<Record<KnownRole, SchemaContractKind>> = {
  gmsb: 'msbvocabulary',
  vnb: 'vnb',
  wim: 'wim',
  wmsb: 'msbvocabulary',
};

// null for an unknown/absent role — the same fail-closed convention every predicate here uses:
// a tenant whose /config never resolved owns no capability, rather than inheriting one.
export function contractFor(role: string | null | undefined): SchemaContractKind | null {
  return role && isKnownRole(role) ? ROLE_CONTRACTS[role] : null;
}

// ── Editing-surface capabilities (Einstellungen § Texte & Vokabular) ──────────────────────────
// Three capabilities, each derived from the contract projection above, so a section's visibility
// is a statement about what the tenant's contract OWNS rather than about which role it is.

// Editable, customeradmin-SAFE reply prose. Keyed to the vnb contract because that is the scope
// of the surface itself: GET/POST /manage/reply-prompts is hard-scoped to
// ContentResolver.VnbTemplatePipeline, whose registry is the editable-component whitelist.
// ⚠ CORRECTION to the older "reply components are a VNB-role concept" reading: MSB roles DO carry
// reply-prompt rows — 42 of them, measured 2026-08-11 across the git-homed and DB-only prompt
// corpus — but ZERO are customeradmin-editable-safe: every one is an internal LLM instruction
// (per-category task, classification rules, output format, persona, FAQ, business rules). So the
// capability is "carries editable-SAFE reply rows", not "carries reply rows", and it is vnb-only
// TODAY because MSB's prose is internal-only — not because reply prose is vnb-shaped. The MSB
// sign-off block's safe, fact-level surface is the CUSTOMER_* config keys (Firmendaten), which is
// a different table and already has its own tab.
export function ownsEditableReplyProse(role: string | null | undefined): boolean {
  return contractFor(role) === 'vnb';
}

// The answer catalog (vnb_acts) and the routing rules — both vnb-contract data structures with no
// counterpart under the other contracts.
export function ownsActsAndRouting(role: string | null | undefined): boolean {
  return contractFor(role) === 'vnb';
}

// The classification-wording rail (vocabulary_overrides, GET/POST /manage/vocabulary).
// THE CAPABILITY IS "SERVES A VOCABULARY", and every contract kind does: core applies the overlay
// once, OUTSIDE the contract switch (VocabularyOverlayService via TryBuildSchemaViewAsync), so
// Vnb / Wim / MsbVocabulary all produce the same VocabularyPayload and all inherit the rail. The
// catalog this screen renders is driven by whatever SEED vocabulary the tenant serves, so the
// surface functions identically under all three.
//
// ⚠ THIS WAS A KINDS-GATE (`=== 'msbvocabulary'`) UNTIL 2026-08-11, and the correction is worth
// stating because the shape recurs: that gate dressed a TEMPORAL fact in STRUCTURAL clothes. The
// reason wim was excluded was that wim was mid-transition at the time the scope was ruled — a fact
// about a date, not about the contract — but it was written as a claim about which contract kinds
// own a wording rail, where it then read as a permanent property and would have kept excluding
// roles for a reason that had already expired. A temporal restriction that must not be forgotten
// belongs in a mechanism that can NOTICE it (the applier's shadowed-key report, core side), never
// in a capability predicate that will simply keep saying no.
//
// Fail-closed is unchanged: contractFor returns null for an unknown/absent role, so a tenant whose
// /config never resolved still owns nothing.
export function ownsEditableVocabulary(role: string | null | undefined): boolean {
  return contractFor(role) !== null;
}

// Whether the generalized "Texte & Vokabular" tab has any section to host at all. The tab is the
// sum of its sections — a role that owns neither must not see an empty shell.
export function hostsTextOrVocabularyEditing(role: string | null | undefined): boolean {
  return ownsEditableReplyProse(role) || ownsEditableVocabulary(role);
}
